import React, { useEffect, useState, useRef } from "react";
import ListingCard from "../components/ListingCard";
import FilterPanel from "../components/FilterPanel";
import { listingsService } from "../services/listingsService";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import ReactDOM from "react-dom";

const categories = [
  { id: 'all', name: 'All', emoji: '🏠' },
  { id: 'Pre-natal', name: 'Pre-natal', emoji: '🤰' },
  { id: 'Clothing', name: 'Clothing', emoji: '👕' },
  { id: 'Gear', name: 'Gear', emoji: '🚗' },
  { id: 'Toys', name: 'Toys', emoji: '🧸' },
  { id: 'Feeding', name: 'Feeding', emoji: '🍼' },
  { id: 'Nursery', name: 'Nursery', emoji: '🛏️' },
  { id: 'Other', name: 'Other', emoji: '📦' }
];

// Helper function to check if two age ranges overlap
function isAgeRangeOverlapping(range1, range2) {
  // Convert age ranges to months for comparison
  const parseAgeRange = (range) => {
    if (!range || range === 'All Ages') return { min: 0, max: Infinity };
    
    // Handle custom ranges like "4-6M", "18-24M", "3-5Y"
    const match = range.match(/^(\d+)-(\d+)([MY])$/);
    if (match) {
      const [, min, max, unit] = match;
      const multiplier = unit === 'Y' ? 12 : 1;
      return { min: parseInt(min) * multiplier, max: parseInt(max) * multiplier };
    }
    
    // Handle predefined ranges
    const predefinedRanges = {
      '0-3M': { min: 0, max: 3 },
      '3-6M': { min: 3, max: 6 },
      '6-12M': { min: 6, max: 12 },
      '12-24M': { min: 12, max: 24 },
      '2-4Y': { min: 24, max: 48 },
      '4Y+': { min: 48, max: Infinity }
    };
    
    return predefinedRanges[range] || { min: 0, max: Infinity };
  };
  
  const parsed1 = parseAgeRange(range1);
  const parsed2 = parseAgeRange(range2);
  
  // Check for overlap: ranges overlap if one starts before the other ends
  return parsed1.min < parsed2.max && parsed2.min < parsed1.max;
}

export default function Browse({ searchQuery = "" }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [conditionFilter, setConditionFilter] = useState('all');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [userLat, setUserLat] = useState("");
  const [userLng, setUserLng] = useState("");
  const [geoStatus, setGeoStatus] = useState("");
  const [distanceFilter, setDistanceFilter] = useState('all');
  const [openDropdown, setOpenDropdown] = useState(null); // 'condition', 'price', 'distance', 'sort', or null
  const [preserveScroll, setPreserveScroll] = useState(false); // Flag to preserve scroll position
  const scrollPositionRef = useRef(0); // Store scroll position persistently
  const filterTimeoutRef = useRef(null); // For debouncing filter updates
  const isUserScrollingRef = useRef(false); // Track if user is actively scrolling
  
  // Enhanced filter state
  const [filters, setFilters] = useState({
    priceRange: [0, 1000],
    maxDistance: 50,
    category: '',
    condition: '',
    ageRange: '',
    tags: [],
    sortBy: 'newest'
  });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  // Helper to position dropdowns using portal
  const [dropdownPos, setDropdownPos] = useState({});
  const chipRefs = {
    condition: useRef(null),
    price: useRef(null),
    distance: useRef(null),
    sort: useRef(null)
  };
  useEffect(() => {
    if (openDropdown && chipRefs[openDropdown]?.current) {
      const rect = chipRefs[openDropdown].current.getBoundingClientRect();
      setDropdownPos({
        left: rect.left,
        top: rect.bottom + window.scrollY,
        width: rect.width
      });
    }
  }, [openDropdown]);

  // Haversine formula
  function getDistanceKm(lat1, lon1, lat2, lon2) {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    const toRad = (v) => (v * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  useEffect(() => {
    const loadItems = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await listingsService.getListings();
        setItems(data);
      } catch (error) {
        console.error('Failed to load listings:', error);
        setError("Failed to load listings. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    loadItems();
  }, []);

  // Track scroll position in content area (not page scroll)
  useEffect(() => {
    let scrollTimeout;
    
    const handleScroll = (e) => {
      // Mark that user is actively scrolling
      isUserScrollingRef.current = true;
      
      // Clear any existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // Set timeout to mark scrolling as finished
      scrollTimeout = setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 150);
      
      // Get scroll position from the main content element
      const mainContent = document.querySelector('.main-content');
      if (mainContent) {
        const currentScroll = mainContent.scrollTop;
        const scrollHeight = mainContent.scrollHeight;
        const clientHeight = mainContent.clientHeight;
        
        // Don't update if we're at the very top or bottom
        const isAtTop = currentScroll <= 0;
        const isAtBottom = currentScroll + clientHeight >= scrollHeight - 10;
        
        if (!isAtTop && !isAtBottom) {
          scrollPositionRef.current = currentScroll;
        }
      }
    };

    // Listen for scroll on the main content element instead of window
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      if (mainContent) {
        mainContent.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  // Consolidated filtering logic with debouncing to prevent scroll position resets
  useEffect(() => {
    // Clear any existing timeout
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }

    // Store current scroll position
    const currentScrollY = scrollPositionRef.current;

    // Debounce the filtering to prevent rapid updates
    filterTimeoutRef.current = setTimeout(() => {
      let filtered = items.map(item => {
        if (userLat && userLng && item.latitude && item.longitude) {
          return {
            ...item,
            distance_km: getDistanceKm(Number(userLat), Number(userLng), Number(item.latitude), Number(item.longitude))
          };
        } else {
          return { ...item };
        }
      });

      // Filter out own listings if user is logged in
      if (user) {
        filtered = filtered.filter(item => item.user_id !== user.id);
      }

      // Apply enhanced filters first (from FilterPanel)
      if (filters.category) {
        filtered = filtered.filter(item => item.category === filters.category);
      }
      if (filters.condition) {
        filtered = filtered.filter(item => item.condition === filters.condition);
      }
      if (filters.ageRange) {
        filtered = filtered.filter(item => {
          if (!item.age_range) return false;
          
          // Handle "All Ages" - matches everything
          if (item.age_range === 'All Ages' || filters.ageRange === 'All Ages') {
            return true;
          }
          
          // Exact match
          if (item.age_range === filters.ageRange) {
            return true;
          }
          
          // Check for overlapping ranges using a helper function
          return isAgeRangeOverlapping(item.age_range, filters.ageRange);
        });
      }
      if (filters.tags && filters.tags.length > 0) {
        filtered = filtered.filter(item => {
          if (!item.tags || item.tags.length === 0) return false;
          // Check if any of the selected tags match any of the item's tags
          return filters.tags.some(tag => item.tags.includes(tag));
        });
      }
      if (filters.priceRange[0] > 0 || filters.priceRange[1] < 1000) {
        filtered = filtered.filter(item => {
          const price = item.price_cents / 100;
          return price >= filters.priceRange[0] && price <= filters.priceRange[1];
        });
      }
      if (userLat && userLng && filters.maxDistance < 50) {
        filtered = filtered.filter(item => typeof item.distance_km === 'number' && item.distance_km <= filters.maxDistance);
      }

      // Apply legacy filters (from quick filter bar)
      if (selectedCategory !== 'all' && !filters.category) {
        filtered = filtered.filter(item => item.category === selectedCategory);
      }
      if (conditionFilter !== 'all' && !filters.condition) {
        filtered = filtered.filter(item => item.condition === conditionFilter);
      }
      if (minPrice !== '') {
        filtered = filtered.filter(item => (item.price_cents / 100) >= parseFloat(minPrice));
      }
      if (maxPrice !== '') {
        filtered = filtered.filter(item => (item.price_cents / 100) <= parseFloat(maxPrice));
      }
      if (userLat && userLng && distanceFilter !== 'all' && filters.maxDistance >= 50) {
        const maxKm = Number(distanceFilter);
        filtered = filtered.filter(item => typeof item.distance_km === 'number' && item.distance_km <= maxKm);
      }

      // Apply search query
      if (searchQuery.trim() !== "") {
        const q = searchQuery.trim().toLowerCase();
        filtered = filtered.filter(item =>
          item.title.toLowerCase().includes(q) ||
          (item.description && item.description.toLowerCase().includes(q))
        );
      }

      // Sort - prioritize enhanced filters sort, fallback to legacy sort
      const currentSortBy = filters.sortBy || sortBy;
      if (currentSortBy === 'price_low' || currentSortBy === 'price-asc') {
        filtered = [...filtered].sort((a, b) => a.price_cents - b.price_cents);
      } else if (currentSortBy === 'price_high' || currentSortBy === 'price-desc') {
        filtered = [...filtered].sort((a, b) => b.price_cents - a.price_cents);
      } else if (currentSortBy === 'distance' && userLat && userLng) {
        filtered = [...filtered].sort((a, b) => (a.distance_km || Infinity) - (b.distance_km || Infinity));
      } else if (currentSortBy === 'oldest') {
        filtered = [...filtered].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      } else {
        // Newest (default)
        filtered = [...filtered].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      }

      setFilteredItems(filtered);

      // Restore scroll position in content area (not page scroll)
      if (!isUserScrollingRef.current && currentScrollY > 100) {
        // Find the main content element to scroll within
        const mainContent = document.querySelector('.main-content');
        if (mainContent) {
          setTimeout(() => {
            mainContent.scrollTo({ top: currentScrollY, behavior: 'instant' });
          }, 100);
        }
      }
      
      // Legacy scroll restoration (disabled since we're using content scroll now)
      if (false && !isUserScrollingRef.current && currentScrollY > 100) {
        const currentScroll = window.scrollY;
        const documentHeight = document.documentElement.scrollHeight;
        const windowHeight = window.innerHeight;
        const isNearBottom = currentScroll + windowHeight >= documentHeight - 100;
        const isNearTop = currentScroll < 100;
        
        // Don't restore scroll if user is at top or bottom of page
        if (!isNearBottom && !isNearTop && Math.abs(currentScroll - currentScrollY) > 100) {
          setTimeout(() => {
            // Double-check that user is still not scrolling and we still need to restore
            if (!isUserScrollingRef.current) {
              const newScroll = window.scrollY;
              if (Math.abs(newScroll - currentScrollY) > 50) {
                window.scrollTo({ top: currentScrollY, behavior: 'instant' });
              }
            }
          }, 100);
        }
      }
    }, 100); // 100ms debounce delay - faster response

    // Cleanup timeout on unmount
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, [items, filters, searchQuery, selectedCategory, conditionFilter, minPrice, maxPrice, sortBy, userLat, userLng, distanceFilter]);


  // Helper: count active filters
  const activeFilterCount = [conditionFilter !== 'all', minPrice, maxPrice, distanceFilter !== 'all'].filter(Boolean).length;
  const anyFilterActive = activeFilterCount > 0;

  if (loading) {
    return (
      <div className="container-center mx-auto">
        {/* Loading Header */}
        <div className="mb-8">
          <div className="h-8 bg-slate-200 rounded-lg loading-skeleton mb-2"></div>
          <div className="h-4 bg-slate-200 rounded-lg loading-skeleton w-2/3"></div>
        </div>
        
        {/* Loading Grid */}
        <div className="list-grid">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="listing-card">
              <div className="h-48 bg-slate-200 loading-skeleton"></div>
              <div className="p-4 space-y-3">
                <div className="h-4 bg-slate-200 rounded loading-skeleton"></div>
                <div className="h-3 bg-slate-200 rounded loading-skeleton w-2/3"></div>
                <div className="flex justify-between items-center">
                  <div className="h-6 bg-slate-200 rounded loading-skeleton w-16"></div>
                  <div className="h-4 bg-slate-200 rounded loading-skeleton w-12"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container-center mx-auto">
      {/* Page Header */}
      <div className="mb-4 sm:mb-8 text-center">
        <h1 className="text-lg sm:text-responsive-xl font-bold text-slate-900 mb-1 sm:mb-2">
          {t('page.browse')}
        </h1>
      </div>

      {/* Stats Bar with Filter Button */}
      <div className="mb-4 sm:mb-6 p-2 sm:p-4 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/40">
        <div className="flex items-center justify-between text-xs sm:text-sm">
          <div className="flex items-center gap-2">
            <span className="text-slate-600">Showing</span>
            <span className="font-semibold text-slate-900">{filteredItems.length} items</span>
          </div>
          
          {/* Filter Button - Icon Only */}
          <button
            className={`relative p-2 rounded-lg font-medium text-sm border flex items-center transition-all duration-200 ${
              anyFilterActive 
                ? 'bg-orange-50 border-orange-400 text-orange-700' 
                : 'bg-white/80 border-slate-200 text-slate-700 hover:bg-white hover:shadow-md'
            }`}
            onClick={() => setIsFilterPanelOpen(true)}
            title="Advanced Filters"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[16px] text-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="mb-4 sm:mb-6">
        <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-all duration-200 ${
                selectedCategory === category.id
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'bg-white/80 text-slate-700 hover:bg-white hover:shadow-md border border-slate-200'
              }`}
            >
              <span className="text-base">{category.emoji}</span>
              <span>{category.name}</span>
              {category.id !== 'all' && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  selectedCategory === category.id
                    ? 'bg-white/20 text-white'
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {items.filter(item => item.category === category.id).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>


      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl mb-6">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xs">!</span>
            </div>
            <div className="text-sm text-red-800">{error}</div>
          </div>
        </div>
      )}

      {/* Items Grid */}
      {filteredItems.length === 0 && !error ? (
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
            <span className="text-4xl">👶</span>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            {selectedCategory === 'all' ? 'No items found' : `No ${categories.find(c => c.id === selectedCategory)?.name.toLowerCase()} items`}
          </h3>
          <p className="text-slate-600 mb-6">
            {selectedCategory === 'all' 
              ? 'Be the first to list an item in your area!' 
              : `No ${categories.find(c => c.id === selectedCategory)?.name.toLowerCase()} items available yet.`
            }
          </p>
          <button 
            className="btn-primary"
            onClick={() => window.location.href = '/create'}
          >
            List an Item
          </button>
        </div>
      ) : (
        <div className="list-grid">
          {filteredItems.map((item, index) => (
            <div key={item.id} style={{ animationDelay: `${index * 100}ms` }}>
              <ListingCard item={item} />
            </div>
          ))}
        </div>
      )}
      
      {/* Enhanced Filter Panel */}
      <FilterPanel
        filters={filters}
        setFilters={setFilters}
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        categories={categories.slice(1).map(c => c.name)} // Exclude 'All'
        conditions={['excellent', 'good', 'fair', 'poor']}
        ageRanges={['0-3M', '3-6M', '6-12M', '12-24M', '2-4Y', '4Y+', 'All Ages']}
      />
    </div>
  );
}